/* @usb-shield/components-react-versioned v1.0.0 © 2020 U.S. Bank - UXTechnology */
import React from 'react';
import PropTypes from 'prop-types';
import { USBIcon } from '@usb-shield/react-icons';

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var _extends_1 = createCommonjsModule(function (module) {
function _extends(){module.exports=_extends=Object.assign||function(target){for(var i=1;i<arguments.length;i++){var source=arguments[i];for(var key in source){if(Object.prototype.hasOwnProperty.call(source,key)){target[key]=source[key];}}}return target;};return _extends.apply(this,arguments);}module.exports=_extends;
});

/**
 * Button
 *
 * @param {String} id - id attribute
 * @param {String} name - Name attribute
 * @param {String} value - Value attribute
 * @param {String} addClasses - Class name for styling
 * @param {String} variant - The variant (loud, primary, secondary, utility, text ).
 * @param {String} type - The HTML5 button type (Button, Submit, Reset).
 * @param {String} size - Small, Default or Large.
 * @param {Boolean} ariaHidden - Becomes the ID of the input and htmlFor attribute of the label
 * @param {String} title - custom title.
 * @param {Object} iconAssistiveText - Assistive text for accessibility.
 * @param {String} iconViewBox - Viewbox size for icon.
 * @param {(Object|Array)} iconData - Icon SVG data.
 * @param {Function} [handleClick] - An onClick handler.
 * @param {Function} [forwardedRef] - The ref from a parent component.
 * @param {String} ariaLabel - Optional offscreen label for a11y.
 * @param {Element} children - The button text.
 * @param {Boolean} disabled - Optional disabled prop.
 * @param {String} dataTestId - dataTestId holds unique test id
 * @param {Object} spacing - spacing to control margin and padding
 * @param {Object} addProps - additional Props
 *
 * @returns {HTMLElement}
 */var USBButton=function USBButton(_ref){var id=_ref.id,name=_ref.name,addClasses=_ref.addClasses,_ref$variant=_ref.variant,variant=_ref$variant===void 0?'text':_ref$variant,_ref$type=_ref.type,type=_ref$type===void 0?'button':_ref$type,_ref$size=_ref.size,size=_ref$size===void 0?'default':_ref$size,_ref$ariaHidden=_ref.ariaHidden,ariaHidden=_ref$ariaHidden===void 0?true:_ref$ariaHidden,title=_ref.title,iconAssistiveText=_ref.iconAssistiveText,iconViewBox=_ref.iconViewBox,_ref$iconData=_ref.iconData,iconData=_ref$iconData===void 0?null:_ref$iconData,_ref$forwardedRef=_ref.forwardedRef,forwardedRef=_ref$forwardedRef===void 0?null:_ref$forwardedRef,_ref$handleClick=_ref.handleClick,handleClick=_ref$handleClick===void 0?null:_ref$handleClick,_ref$ariaLabel=_ref.ariaLabel,ariaLabel=_ref$ariaLabel===void 0?null:_ref$ariaLabel,_ref$children=_ref.children,children=_ref$children===void 0?null:_ref$children,_ref$disabled=_ref.disabled,disabled=_ref$disabled===void 0?false:_ref$disabled,_ref$value=_ref.value,value=_ref$value===void 0?null:_ref$value,dataTestId=_ref.dataTestId,iconColor=_ref.iconColor,_ref$spacing=_ref.spacing,spacing=_ref$spacing===void 0?null:_ref$spacing,_ref$addProps=_ref.addProps,addProps=_ref$addProps===void 0?null:_ref$addProps;var iconName=title?title:'icon-sys-add';return/*#__PURE__*/React.createElement("button",_extends_1({ref:forwardedRef,id:id,className:"usb-button button--".concat(variant," button--").concat(size," ").concat(addClasses),name:name,type:type,"aria-label":ariaLabel?ariaLabel:null,"data-test":dataTestId?dataTestId:null,onClick:handleClick,disabled:disabled,value:value?value:null,style:{margin:spacing&&spacing.margin&&spacing.margin,padding:spacing&&spacing.padding&&spacing.padding}},addProps),iconData!==null?/*#__PURE__*/React.createElement(USBIcon,{assistiveText:iconAssistiveText,ariaHidden:ariaHidden,colorVariant:iconColor,size:"16",viewBox:iconViewBox,title:iconName,xmlns:"http://www.w3.org/2000/svg",data:iconData}):iconData,children);};USBButton.propTypes={/**
   * ID on parent container
   */id:PropTypes.string,/**
   * Name on button
   */name:PropTypes.string,/**
   * Value of button
   */value:PropTypes.string,/**
   * Any classes you want to add to the parent container
   */addClasses:PropTypes.string,/**
   * The variant (loud, primary, secondary, utility, text )
   */variant:PropTypes.string,/**
   * TThe HTML5 button type (Button, Submit, Reset)
   */type:PropTypes.string,/**
   * Small, Default or Large
   */size:PropTypes.string,/**
   * Assistive text for accessibility.
   * This object is merged with the default props object on every render.
   * * label: Text that is visually hidden but read aloud by screenreaders to tell the user what the icon means.
   */iconAssistiveText:PropTypes.object,/**
   * Viewbox size for icon.
   */iconViewBox:PropTypes.string,/**
   * The icon data, formatted as a json object
   */iconData:PropTypes.oneOfType([PropTypes.object,PropTypes.array]),/**
   * aria-hidden true of false
   */ariaHidden:PropTypes.bool,/**
   * Optional offscreen label for a11y
   */ariaLabel:PropTypes.string,/**
   * Title attribute for the icon container
   */title:PropTypes.string,/**
   * Ref on parent container
   */forwardedRef:PropTypes.oneOfType([PropTypes.func,PropTypes.shape({current:PropTypes.any})]),/**
   * An onClick handler
   */handleClick:PropTypes.func,/**
   * The button text
   */children:PropTypes.oneOfType([PropTypes.element,PropTypes.elementType,PropTypes.array,PropTypes.object]),/**
   * Optional disabled prop
   */disabled:PropTypes.bool,dataTestId:PropTypes.string,iconColor:PropTypes.string,spacing:PropTypes.object,addProps:PropTypes.object};USBButton.defaultProps={id:'',name:'',addClasses:'',variant:'text',type:'button',size:'default',ariaLabel:null,forwardedRef:null,handleClick:null,children:null,disabled:false,dataTestId:'',iconColor:'default',spacing:null,addProps:null};

export default USBButton;
